"use client";

import { type ReactNode } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "~/componentsDS/Tab";
import { cn } from "~/lib/utils";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { getTextFromNode } from "~/utils/getTextFromNode";
import type { InfoBadgeEventData } from "~/componentsDS/InfoBadge";

export interface ContentTab {
    value: string;
    label: ReactNode;
    content: ReactNode;
    triggerClassName?: string;
    contentClassName?: string;
}

export interface ContentTabsProps {
    tabs: ContentTab[];
    defaultValue?: string;
    value?: string;
    onValueChange?: (value: string) => void;
    onTabChange?: (value: string) => void;
    className?: string;
    listClassName?: string;
    triggerClassName?: string;
    /** Tracking payload sent when a tab is clicked. */
    eventData?: InfoBadgeEventData;
}

const ContentTabs = ({
    tabs,
    defaultValue,
    value,
    onValueChange,
    onTabChange,
    className,
    listClassName,
    triggerClassName,
    eventData,
}: ContentTabsProps) => {
    const sendEvent = useTrackEvent();
    const firstTab = tabs[0];
    if (!firstTab) return null;

    const fallbackDefault = defaultValue ?? firstTab.value;

    const handleTabChange = (tabValue: string) => {
        if (eventData) {
            const {
                event: eventName = "user_interaction",
                meta_data,
                ...eventValue
            } = eventData;

            const tabLabel = tabs.find((tab) => tab.value === tabValue)?.label;
            const clickText = getTextFromNode(tabLabel) || tabValue;

            sendEvent({
                event: eventName,
                value: {
                    ...eventValue,
                    meta_data: {
                        click_text: clickText,
                        ...meta_data,
                    },
                },
            });
        }

        onTabChange?.(tabValue);
    };

    return (
        <Tabs
            value={value}
            defaultValue={fallbackDefault}
            onValueChange={onValueChange}
            className={cn("w-full pb-14", className)}
        >
            <TabsList className={listClassName}>
                {tabs.map((tab) => (
                    <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        onTabChange={handleTabChange}
                        className={cn(triggerClassName, tab.triggerClassName)}
                    >
                        {tab.label}
                    </TabsTrigger>
                ))}
            </TabsList>

            {tabs.map((tab) => (
                <TabsContent
                    key={tab.value}
                    value={tab.value}
                    className={tab.contentClassName}
                >
                    {tab.content}
                </TabsContent>
            ))}
        </Tabs>
    );
};

export default ContentTabs;
